/**
 * Circuit breaker with configurable failure threshold, cooldown, and half-open retry.
 *
 * States: CLOSED (normal) → OPEN (blocking) → HALF_OPEN (testing) → CLOSED
 *
 * Escalating backoff: each time the breaker re-trips from half-open, the cooldown
 * escalates through configurable tiers (default: 5m → 10m → 30m → 60m).
 * After maxTripCount trips the source is suspended for the session.
 */

export interface CircuitBreakerConfig {
  failureThreshold: number;
  cooldownMs: number;
  halfOpenMax?: number;
  /** Escalating cooldown tiers in ms. Defaults to [5m, 10m, 30m, 60m]. */
  cooldownTiers?: number[];
  /** Number of trips before permanent session suspension. Default: 6. */
  maxTripCount?: number;
}

type CircuitState = 'closed' | 'open' | 'half_open' | 'suspended';

export class CircuitBreaker {
  private state: CircuitState = 'closed';
  private failureCount = 0;
  private lastFailure = 0;
  private halfOpenAttempts = 0;
  private tripCount = 0;

  private readonly failureThreshold: number;
  private readonly cooldownTiers: number[];
  private readonly halfOpenMax: number;
  private readonly maxTripCount: number;

  constructor(config: CircuitBreakerConfig) {
    this.failureThreshold = config.failureThreshold;
    this.halfOpenMax = config.halfOpenMax ?? 1;
    this.cooldownTiers = config.cooldownTiers ?? [
      config.cooldownMs,          // tier 0: initial (e.g. 5 min)
      config.cooldownMs * 2,      // tier 1: 10 min
      config.cooldownMs * 6,      // tier 2: 30 min
      config.cooldownMs * 12,     // tier 3: 60 min
    ];
    this.maxTripCount = config.maxTripCount ?? 6;
  }

  /** Execute a function through the circuit breaker. */
  async execute<T>(fn: () => Promise<T>): Promise<T> {
    if (!this.canExecute()) {
      throw new Error(this.state === 'suspended'
        ? 'Circuit breaker suspended for session'
        : 'Circuit breaker is open');
    }

    if (this.state === 'open') {
      this.state = 'half_open';
      this.halfOpenAttempts = 0;
    }

    try {
      const result = await fn();
      this.onSuccess();
      return result;
    } catch (err) {
      this.onFailure();
      throw err;
    }
  }

  /** Check if the circuit allows execution. */
  canExecute(): boolean {
    if (this.state === 'suspended') return false;
    if (this.state === 'closed') return true;
    if (this.state === 'half_open') return this.halfOpenAttempts < this.halfOpenMax;
    // state === 'open'
    const elapsed = Date.now() - this.lastFailure;
    return elapsed >= this.getCurrentCooldown();
  }

  /** Get current circuit state. */
  getState(): CircuitState {
    return this.state;
  }

  /** Get the current cooldown duration based on trip count. */
  getCurrentCooldown(): number {
    const tierIndex = Math.min(this.tripCount, this.cooldownTiers.length - 1);
    return this.cooldownTiers[tierIndex];
  }

  /** How many times the breaker has tripped. */
  getTripCount(): number {
    return this.tripCount;
  }

  /** Reset the circuit breaker to closed state. */
  reset(): void {
    this.state = 'closed';
    this.failureCount = 0;
    this.halfOpenAttempts = 0;
    this.tripCount = 0;
  }

  private onSuccess(): void {
    this.failureCount = 0;
    this.state = 'closed';
    this.halfOpenAttempts = 0;
    // Don't reset tripCount on success — keeps backoff memory
  }

  private onFailure(): void {
    this.failureCount++;
    this.lastFailure = Date.now();

    if (this.state === 'half_open') {
      this.halfOpenAttempts++;
      this.tripCount++;
      if (this.tripCount >= this.maxTripCount) {
        this.state = 'suspended';
      } else {
        this.state = 'open';
      }
    } else if (this.failureCount >= this.failureThreshold) {
      this.tripCount++;
      if (this.tripCount >= this.maxTripCount) {
        this.state = 'suspended';
      } else {
        this.state = 'open';
      }
    }
  }
}

// --- Generic circuit breaker with bound function and cache fallback ---

type FetchCircuitState = 'closed' | 'open' | 'half-open';

interface CircuitBreakerOptions {
  failureThreshold?: number;  // default 3
  resetTimeoutMs?: number;    // default 30000
  cacheFallback?: boolean;    // default true
}

/**
 * Generic circuit breaker that wraps a single async function.
 * Tracks failures and opens the circuit when the threshold is reached.
 * Optionally caches the last successful result as a fallback when the circuit is open.
 */
export class FetchCircuitBreaker<T> {
  private state: FetchCircuitState = 'closed';
  private failureCount = 0;
  private lastFailureTime = 0;
  private cachedResult: T | null = null;

  private readonly failureThreshold: number;
  private readonly resetTimeoutMs: number;
  private readonly cacheFallback: boolean;

  constructor(
    private name: string,
    private fn: () => Promise<T>,
    options: CircuitBreakerOptions = {},
  ) {
    this.failureThreshold = options.failureThreshold ?? 3;
    this.resetTimeoutMs = options.resetTimeoutMs ?? 30_000;
    this.cacheFallback = options.cacheFallback ?? true;
  }

  /** Execute the wrapped function through the circuit breaker. */
  async call(): Promise<T> {
    // If open, check if reset timeout has passed → transition to half-open
    if (this.state === 'open') {
      const elapsed = Date.now() - this.lastFailureTime;
      if (elapsed >= this.resetTimeoutMs) {
        this.state = 'half-open';
      } else {
        // Still open — return cache if available, otherwise throw
        if (this.cacheFallback && this.cachedResult !== null) {
          return this.cachedResult;
        }
        throw new Error(`[${this.name}] Circuit breaker is open`);
      }
    }

    // half-open: try once — success closes, failure re-opens
    // closed: normal execution — track failures
    try {
      const result = await this.fn();
      this.cachedResult = result;
      this.failureCount = 0;
      this.state = 'closed';
      return result;
    } catch (err) {
      this.failureCount++;
      this.lastFailureTime = Date.now();

      if (this.state === 'half-open') {
        // Failed during probe — re-open
        this.state = 'open';
      } else if (this.failureCount >= this.failureThreshold) {
        this.state = 'open';
      }

      // Return cached result as fallback if available
      if (this.cacheFallback && this.cachedResult !== null) {
        console.warn(`[${this.name}] Fetch failed, returning cached result`);
        return this.cachedResult;
      }

      throw err;
    }
  }

  /** Get the current circuit state. */
  getState(): FetchCircuitState {
    return this.state;
  }

  /** Reset the circuit breaker to closed state. */
  reset(): void {
    this.state = 'closed';
    this.failureCount = 0;
    this.lastFailureTime = 0;
  }
}
